import { FC, ReactNode, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, styled, IconButton, Tooltip } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useLocation } from 'react-router-dom'

import PageContainerWrapper, { pageRightMargin } from '../PageContainerWrapper'
const PageTitle = styled(Box)(
  ({ theme }) => `
        padding: ${theme.spacing(2)};
`
)

interface PageTitleWrapperProps {
  children?: ReactNode
}

const PageTitleWrapper: FC<PageTitleWrapperProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [canGoBack, setCanGoBack] = useState(false)

  useEffect(() => {
    // Root / primary dashboard screens: no back affordance
    const isRootPath =
      location.pathname === '/' ||
      location.pathname === '/dashboard' ||
      location.pathname === '/dashboard/current-account'

    if (isRootPath) {
      setCanGoBack(false)
      return
    }

    // Same-origin full navigation (referrer still reflects initial load, not SPA pushes)
    let hasSameOriginReferrer = false
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        if (document.referrer !== window.location.href) {
          hasSameOriginReferrer =
            new URL(document.referrer).origin === window.location.origin
        }
      } catch {
        hasSameOriginReferrer = false
      }
    }

    // SPA / in-app navigation increases session history length; referrer often stays empty
    const hasHistoryStack =
      typeof window !== 'undefined' &&
      typeof window.history.length === 'number' &&
      window.history.length > 1

    setCanGoBack(hasHistoryStack || hasSameOriginReferrer)
  }, [location.pathname, location.key, location.search])

  const handleGoBack = () => {
    if (canGoBack) {
      navigate(-1)
    }
    // If disabled (new tab scenario), do nothing - button is already disabled
    // This prevents confusion and follows browser security restrictions
  }

  return (
    <PageTitle
      sx={{
        marginRight: pageRightMargin / 16,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: '#fff', // match your app's background color
        paddingTop: '25px',
        paddingBottom: '25px',
        minHeight: '100px', // Fixed minimum height for consistent vertical positioning
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {canGoBack ? (
            <Tooltip title="Go back">
              <span>
                <IconButton
                  onClick={handleGoBack}
                  sx={{
                    color: '#fff',
                    backgroundColor: '#1976d2',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton
              disabled
              sx={{
                color: 'rgba(0, 0, 0, 0.26)',
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                width: 36,
                height: 36,
                borderRadius: '50%',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          {children}
        </Box>
      </Box>
    </PageTitle>
  );
};

PageTitleWrapper.propTypes = {
  children: PropTypes.node.isRequired,
}

export default PageTitleWrapper
